import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { camelizeKeys } from 'humps';

import type { RootState } from '@/redux/configureStore';

import { validateTestServer } from '../utils';

import type { FetchArgs } from '@reduxjs/toolkit/query';
import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';


const appendQueryStringParam = ( 
  args: string | FetchArgs,
  key: string,
  value: string
): string | FetchArgs => {
  const urlEnd =
    typeof args === 'string'
      ? args
      : (args as FetchArgs).url;

  const updatedUrl = urlEnd.includes('?')
    ? `${urlEnd}&${key}=${value}`
    : `${urlEnd}?${key}=${value}`;

  return typeof args === 'string'
    ? updatedUrl
    : { ...args, url: updatedUrl };
};

const rawBaseQuery = (baseUrl: string) => fetchBaseQuery({
  baseUrl,
  mode: 'cors',
  prepareHeaders: (headers, api) => {
    const { token } = (api.getState() as RootState).auth;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('Accept', 'application/json');
    }
    headers.set('Cache-Control', 'no-cache, no-store');
    return headers;
  },
  timeout: 60000,
  credentials: "include"
});

const customBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  let baseUrl = (api.getState() as RootState).app.route;
  const { providerMode, directMode } = (api.getState() as RootState).auth;

  let customArgs = args;
  const domain = customArgs.body?.domain;

  if (domain) {
    baseUrl = validateTestServer(domain);
  }
  
  if (process.env.NODE_ENV === 'test') {
    baseUrl = 'http://localhost';
  }

  if (providerMode) {
    customArgs = appendQueryStringParam(customArgs, 'provider', 'true');
  }
  if (directMode) {
    customArgs = appendQueryStringParam(customArgs, 'direct', 'true');
  }

  if (baseUrl === null) {
    return {
      error: {
        status: 400,
        statusText: 'Bad Request',
        data: 'No base url received',
      },
    };
  }

  const result = await rawBaseQuery(baseUrl)(customArgs, api, extraOptions);

  if (!result.error && result.data == null) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'Empty response body',
        data: result.data,
      },
    };
  }

  if (result.data) {
    result.data = camelizeKeys(result.data);
  }

  return result;
};

const RETRYABLE_ERROR_STATUSES = [
  'CUSTOM_ERROR',
  'FETCH_ERROR',
  'TIMEOUT_ERROR',
  'PARSING_ERROR',
];

// retries transient failures (empty response body, network hiccups, timeouts,
// server errors) a couple of times before surfacing an error to the UI;
// does not retry real client errors (4xx) since retrying won't help those
const baseQueryWithRetry = retry(customBaseQuery, {
  retryCondition: (error, args, { attempt }) => {
    if (attempt > 2) return false;

    const method =
      typeof args === 'string'
        ? 'GET'
        : ((args as FetchArgs).method ?? 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') return false;

    const status = (error as { status?: unknown })?.status;

    return (
      RETRYABLE_ERROR_STATUSES.includes(status as string) ||
      (typeof status === 'number' && status >= 500)
    );
  },
});

export const baseApi = createApi({
  baseQuery: baseQueryWithRetry,
  endpoints: () => ({}),
  reducerPath: 'api',
});
